<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import TeamTaskDayCard from "@/team-task/components/TeamTaskDayCard.vue";
import {useRoute, useRouter} from "vue-router";
import {useLocalStorage, useMediaQuery, useNow} from "@vueuse/core";
import {CalendarClock, CalendarDays, ChevronLeft, ChevronRight, Ellipsis, Plus} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Skeleton} from "@/core/components/ui/skeleton";
import {Popover, PopoverAnchor, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import AssistantLauncher from "@/ai-assistant/components/AssistantLauncher.vue";
import useAssistantScreen from "@/ai-assistant/composables/useAssistantScreen";
import AgendaLaneGrid from "@/agenda/components/AgendaLaneGrid.vue";
import AgendaMonthGrid from "@/agenda/components/AgendaMonthGrid.vue";
import AgendaAppointmentFormModal from "@/agenda/components/AgendaAppointmentFormModal.vue";
import AgendaAppointmentDetail from "@/agenda/components/AgendaAppointmentDetail.vue";
import AgendaRescheduleDialog from "@/agenda/components/AgendaRescheduleDialog.vue";
import AgendaBookingLinkModal from "@/agenda/components/booking-link/AgendaBookingLinkModal.vue";
import AgendaOptions from "@/agenda/components/AgendaOptions.vue";
import {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import useAgenda from "@/agenda/composables/useAgenda";
import useAgendaOperators from "@/agenda/composables/useAgendaOperators";
import useAgendaPresence from "@/agenda/composables/useAgendaPresence";
import useBookingLinkPresets from "@/agenda/composables/useBookingLinkPresets";
import useFrequentation from "@/agenda/composables/useFrequentation";
import useAppointmentMutations from "@/agenda/composables/useAppointmentMutations";
import useHealthcenterOpening from "@/healthcenter/composables/useHealthcenterOpening";
import FrequentationLegend from "@/agenda/components/frequentation/FrequentationLegend.vue";
import {AgendaLane} from "@/agenda/lib/lanes";
import {accessIdForAppointment, isUnassigned, operatorIdentityOf} from "@/agenda/lib/operators";
import {
    AgendaViewKey,
    labelForView,
    overlapsDay,
    rangeForView,
    shiftForView,
    weekColumnCount
} from "@/agenda/lib/agendaViews";
import {defaultSlotForDay, isPastStart, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {PAST_APPOINTMENT_MESSAGE} from "@/agenda/constants";
import {birthdayGiftApi, customerCertificationApi} from "@/container";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {AgendaRenewalReminder} from "@/core/interfaces/AgendaRenewalReminder";
import {AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import {PatientSmsChoice} from "@/agenda/lib/patientSms";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";
import useAgendaBirthdays from "@/agenda/composables/useAgendaBirthdays";
import AgendaBirthdayPanel from "@/agenda/components/AgendaBirthdayPanel.vue";
import {
    BirthdaySendRecipient,
    birthdaySendRefusalLabel,
    certificationErrorMessage
} from "@/agenda/lib/birthdays";
import {GiftBatchRecipient} from "@/birthday-gift/interfaces";
import useBirthdayGifts from "@/birthday-gift/composables/useBirthdayGifts";
import useBirthdayGiftSettings from "@/birthday-gift/composables/useBirthdayGiftSettings";
import useExperimentalFeatures from "@/core/composables/experimental-features/useExperimentalFeatures";

/**
 * The pharmacy's calendar: toolbar, day/week/month/team grids and the
 * appointment dialogs. The agenda screen mounts it whole; the patient file
 * mounts it scoped to one patient.
 */
const props = withDefaults(defineProps<{
    /**
     * Patient every creation opens with, when the calendar is displayed from
     * their file. Shown in the form and replaceable there.
     */
    pinnedPatient?: SelectedPatient | null;
    /** Keep only this patient's appointments and reminders. */
    filterCustomerId?: number | null;
    /** Without the day-tasks rail, which belongs to the agenda screen alone. */
    compact?: boolean;
}>(), {
    pinnedPatient: null,
    filterCustomerId: null,
    compact: false
});

const emit = defineEmits<{
    /** An appointment was created, edited, moved or cancelled from the grid. */
    (event: 'changed'): void
}>();

const VIEWS: {key: AgendaViewKey; label: string}[] = [
    {key: 'day', label: 'Jour'},
    {key: 'week', label: 'Semaine'},
    {key: 'month', label: 'Mois'},
    {key: 'team', label: 'Équipe'}
];

/**
 * Below the xl breakpoint the toolbar cannot fit the action labels next to the
 * date and the view switch: they collapse to icons and the tooltips carry the
 * wording. The right rail goes at the same breakpoint.
 */
const labelsHidden = useMediaQuery('(max-width: 1279px)');

/** A patient file never draws the rail, whatever the screen width. */
const railHidden = computed(() => props.compact || labelsHidden.value);

const currentDay = ref(new Date());

/** Persisted so the counter reopens on the view it works with. */
const storedView = useLocalStorage<AgendaViewKey>('agenda.view', 'day');

/**
 * A stale or hand-edited preference would otherwise leave the toolbar with no
 * active view and fall through to the day lanes.
 */
const currentView = computed<AgendaViewKey>({
    get: () => VIEWS.some((view) => view.key === storedView.value) ? storedView.value : 'day',
    set: (value) => {
        storedView.value = value;
    }
});

const range = computed(() => rangeForView(currentView.value, currentDay.value));

const {data: appointments, reminders: loadedReminders, error, isLoading, refresh, organizationId} = useAgenda(
    range,
    computed(() => props.filterCustomerId)
);

/**
 * The endpoint only filters the appointments: the reminders of the window,
 * a handful, are narrowed here.
 */
const reminders = computed(() => {
    const customerId = props.filterCustomerId;

    if (!customerId) {
        return loadedReminders.value;
    }

    return loadedReminders.value.filter((reminder) => reminder.customer.id === customerId);
});

const {environment} = storeToRefs(useSystemStore());

/** Access point of the session, for the « À moi » filter of the day tasks. */
const currentAccessId = computed<number | null>(() => environment.value?.id ?? null);
const {operators, isLoading: operatorsLoading} = useAgendaOperators();

/** Presence overlay of the day view: same resolver as the Planning screen. */
const presenceEnabled = useLocalStorage<boolean>('agenda.presence', true);

/**
 * Only the day view draws the rail, so only it pays for the data. The toggle
 * itself stays visible whatever the data says — gating it on the lanes would
 * hide the only control able to turn the overlay back on.
 */
const presenceActive = computed(() => currentView.value === 'day' && presenceEnabled.value);
const {presenceLanes, presenceUsable, presencePending} = useAgendaPresence(currentDay, presenceActive);
const presenceShown = computed(() => presenceUsable.value && presenceLanes.value.length > 0);
const toastStore = useToastStore();
const router = useRouter();
const route = useRoute();

// ----- Birthdays -----

const birthdayPanelOpen = ref<boolean>(false);

/** The day the panel shows: today from the toolbar, any day from a chip. */
const birthdayPanelDay = ref<Date>(new Date());

/**
 * The week and month overlay, off by default: a month of patient data must
 * not be paid on every navigation. Kept across navigation, like the mockup.
 */
const birthdayOverlay = ref<boolean>(false);

/**
 * The day view carries the count in its toolbar, so it needs the birthdays
 * before the panel opens — one day of patients is cheap. The week and month
 * views do not: their overlay is a toggle, and a month of patient data must
 * not be paid on every navigation. A patient file shows one patient's agenda,
 * not the pharmacy's birthdays, so it never pays for them.
 */
const birthdaysEnabled = computed<boolean>(() =>
    !props.compact && (currentView.value === 'day' || birthdayOverlay.value || birthdayPanelOpen.value));

const {birthdays, birthdaysOn, refresh: refreshBirthdays, failed: birthdaysFailed} = useAgendaBirthdays(range, birthdaysEnabled);

/**
 * Offered for the patients no gift targets. Loaded with the panel rather than
 * with the agenda: only the panel needs them.
 */
const {data: allGifts} = useBirthdayGifts();
const {data: giftSettings} = useBirthdayGiftSettings();

const activeGifts = computed(() => (allGifts.value ?? []).filter(gift => gift.active));

const goToGiftCatalog = () => router.push({name: 'patient-administration.communication.birthday-gifts'});

const dayBirthdays = computed(() => birthdaysOn(currentDay.value));

/** What the panel lists: the day it was opened on, not necessarily today. */
const panelBirthdays = computed(() => birthdaysOn(birthdayPanelDay.value));

/** Only handed to the grids when the overlay is on. */
const overlayBirthdays = computed(() => birthdayOverlay.value ? birthdays.value : []);

const openBirthdayPanel = (day: Date) => {
    birthdayPanelDay.value = day;
    birthdayPanelOpen.value = true;
};

const sendingBirthdayIds = ref<number[]>([]);
const sendingAllBirthdays = ref<boolean>(false);

const birthdayCount = computed<number>(() => dayBirthdays.value.length);

/**
 * A mobile typed at the counter is saved as a pre-certification, the same
 * gesture as the patient file's: the number then belongs to the patient, so
 * nobody types it again and next year's automatic dispatch reaches them.
 *
 * Returns false when it could not be saved — the send must not go ahead, it
 * would be routed nowhere.
 */
const certifyTypedPhone = async (customerId: number, phone: string): Promise<boolean> => {
    try {
        await customerCertificationApi().certifyPhone(customerId, phone);

        return true;
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: certificationErrorMessage(error)});
        }

        return false;
    }
};

/**
 * One patient, through the same endpoint as « send to everyone »: it is the
 * only one that carries a gift chosen at the counter and the fallback to plain
 * wishes for a patient no gift targets. The patient file's own send keeps its
 * stricter rules.
 */
const sendBirthday = async ({customerId, giftId, phone}: {
    customerId: number;
    giftId?: number;
    phone?: string;
}) => {
    if (!organizationId.value) {
        return;
    }

    sendingBirthdayIds.value = [...sendingBirthdayIds.value, customerId];

    try {
        if (phone && !await certifyTypedPhone(customerId, phone)) {
            return;
        }

        const result = await birthdayGiftApi().sendBatch(organizationId.value, [{
            customer_id: customerId,
            ...(giftId ? {birthday_gift_id: giftId} : {})
        }]);

        await refreshBirthdays();

        const outcome = result.outcomes[0];

        if (outcome?.sent) {
            toastStore.showSuccess({content: 'Le message d\'anniversaire a été envoyé'});
        } else {
            toastStore.showError({content: birthdaySendRefusalLabel(outcome?.reason ?? null)});
        }
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error, 'Le message n\'a pas pu être envoyé')});
        }
    } finally {
        sendingBirthdayIds.value = sendingBirthdayIds.value.filter(id => id !== customerId);
    }
};

const sendAllBirthdays = async (recipients: BirthdaySendRecipient[]) => {
    if (!organizationId.value) {
        return;
    }

    sendingAllBirthdays.value = true;

    try {
        // Saved one by one before the batch leaves: a number that cannot be
        // saved takes its patient out of the lot rather than sending them a
        // message routed nowhere.
        const sendable: GiftBatchRecipient[] = [];
        let unsaved = 0;

        for (const {phone, ...recipient} of recipients) {
            if (phone && !await certifyTypedPhone(recipient.customer_id, phone)) {
                unsaved++;
                continue;
            }

            sendable.push(recipient);
        }

        if (!sendable.length) {
            return;
        }

        const result = await birthdayGiftApi().sendBatch(organizationId.value, sendable);
        const refused = result.outcomes.filter(outcome => !outcome.sent).length + unsaved;

        await refreshBirthdays();

        if (result.sent_count > 0) {
            toastStore.showSuccess({
                content: `${result.sent_count} ${result.sent_count > 1 ? 'messages ont été envoyés' : 'message a été envoyé'}`
            });
        }

        // Said explicitly rather than folded into the success: a batch that
        // reached nobody must not look like it worked.
        if (refused > 0) {
            toastStore.showError({
                content: `${refused} ${refused > 1 ? 'patients n\'ont pas pu être servis' : 'patient n\'a pas pu être servi'}`
            });
        }
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error, 'Les messages n\'ont pas pu être envoyés')});
        }
    } finally {
        sendingAllBirthdays.value = false;
    }
};

/* ----------------------------- frequentation ----------------------------- */

/** Persisted: showing the expected load is a per-workstation habit. */
const frequentationVisible = useLocalStorage<boolean>('agenda.frequentation', true);

/**
 * How the load overlays each view: a strip beside the hours where every
 * column shares one day (day, team), the column backgrounds in the week
 * view, nothing in the month view.
 */
const frequentationDisplay = computed<'column' | 'background' | undefined>(() => {
    if (currentView.value === 'day' || currentView.value === 'team') {
        return 'column';
    }

    return currentView.value === 'week' ? 'background' : undefined;
});

/**
 * The week whose load is fetched: the displayed one, whenever the view can
 * show it. Fetched even while hidden — the toggle only appears once the
 * pharmacy is known to have data, so gating the fetch on it would hide the
 * only control able to turn the overlay back on.
 */
const frequentationWeekStart = computed(() => {
    return frequentationDisplay.value ? parseAgendaDate(currentDay.value).startOf('week') : null;
});

const {frequentation, hasData: hasFrequentation} = useFrequentation(() => frequentationWeekStart.value);
const frequentationActive = computed(() => frequentationVisible.value && hasFrequentation.value && !!frequentationDisplay.value);

/* ----------------------------- closing hours ----------------------------- */

/**
 * The pharmacy's opening sheet, to grey its closing hours where a column is
 * a day. The team view, whose columns are collaborators, keeps the day's
 * presence rail as its only time cue; the month view has no hours at all.
 */
const {opening: healthcenterOpening} = useHealthcenterOpening();
const closingHoursShown = computed(() => (currentView.value === 'day' || currentView.value === 'week') && !!healthcenterOpening.value);

/**
 * Reactive clock, so "today" stays right on a workstation left open
 * across midnight.
 */
const now = useNow({interval: 60 * 1000});

const weekColumns = computed(() => weekColumnCount(appointments.value ?? [], currentDay.value, reminders.value));
const dayLabel = computed(() => labelForView(currentView.value, currentDay.value, weekColumns.value));
const isToday = computed(() => parseAgendaDate(currentDay.value).isSame(parseAgendaDate(now.value), 'day'));

/**
 * What the assistant is told about this screen.
 *
 * The launcher sits in this toolbar; without this the question would leave
 * saying nothing about the day the pharmacist is looking at.
 *
 * The embedded calendar of a patient file declares nothing, and its launcher
 * is hidden. What is on screen there is one patient's appointments, which the
 * planning assistant answers nothing about — and the drawer can be open from
 * an earlier screen while the pharmacist walks into that file, so the context
 * has to be dropped as well as the button.
 */
useAssistantScreen(() => props.compact ? null : ({
    screen: 'Agenda',
    window: dayLabel.value
}));

const shiftDay = (direction: number) => {
    currentDay.value = shiftForView(currentView.value, currentDay.value, direction);
};

/**
 * Columns of the current view: the single day, the days of the week, or one
 * per collaborator. Appointments are grouped client-side from the one range
 * the endpoint already returned.
 */
const lanes = computed<AgendaLane[]>(() => {
    const all = appointments.value ?? [];

    if (currentView.value === 'week') {
        const start = parseAgendaDate(currentDay.value).startOf('week');

        return Array.from({length: weekColumns.value}, (_, index) => {
            const day = start.add(index, 'day');
            // Overlap, not start-day equality: a night appointment belongs to
            // both days, and positionLanes clamps it to each column's segment.
            const ofDay = all.filter((appointment) => overlapsDay(appointment, day));

            return {
                key: day.format('YYYY-MM-DD'),
                label: day.format('ddd D'),
                sublabel: ofDay.length ? `${ofDay.length} RDV` : 'Aucun RDV',
                day: day.toDate(),
                appointments: ofDay
            };
        });
    }

    if (currentView.value === 'team') {
        /*
         * The access point each appointment belongs to, resolved once. Going
         * through the resolver rather than comparing columns per lane is what
         * keeps an older row in ONE column: matching on the
         * operator's user id alone would have put it in every access that user
         * holds, and the pharmacies where that happens are exactly those the
         * migration left unbackfilled.
         */
        const laneOf = new Map(all.map((appointment) => [
            appointment.id,
            accessIdForAppointment(appointment, operators.value)
        ]));

        // One column per member of the current staff, members without a user
        // account included since an appointment designates its operator by
        // access point.
        const staffLanes = operators.value.map((operator) => {
            const ofOperator = all.filter((appointment) => laneOf.get(appointment.id) === operator.accessId);

            return {
                key: operator.key,
                label: operator.name,
                sublabel: ofOperator.length ? `${ofOperator.length} RDV` : 'Aucun RDV',
                day: currentDay.value,
                operatorAccessId: operator.accessId,
                readonly: !operator.assignable,
                appointments: ofOperator
            };
        });

        /*
         * Whatever no staff column claimed and that still names somebody: an
         * operator who left the pharmacy, reached either by access point or —
         * for the older rows — by user id alone. Without
         * these columns their appointments would show in every other view but
         * silently vanish from this one. Read-only: a click on the lane would
         * book a new appointment onto somebody who left.
         */
        const claimed = new Set(staffLanes.flatMap((lane) => lane.appointments.map((appointment) => appointment.id)));
        const orphanLanes = new Map<string | number, AgendaLane>();

        all.forEach((appointment) => {
            if (claimed.has(appointment.id)) {
                return;
            }

            const identity = operatorIdentityOf(appointment);

            if (!identity) {
                return;
            }

            const lane = orphanLanes.get(identity.key) ?? {
                key: identity.key,
                label: identity.name,
                sublabel: '',
                day: currentDay.value,
                operatorAccessId: identity.accessId,
                readonly: true,
                appointments: []
            };

            lane.appointments.push(appointment);
            lane.sublabel = `${lane.appointments.length} RDV`;
            orphanLanes.set(identity.key, lane);
        });

        // Unassigned appointments would otherwise be invisible in this view.
        const unassigned = all.filter((appointment) => isUnassigned(appointment));

        return unassigned.length
            ? [...staffLanes, ...orphanLanes.values(), {
                key: 'unassigned',
                label: 'Non attribué',
                sublabel: `${unassigned.length} RDV`,
                day: currentDay.value,
                appointments: unassigned
            }]
            : [...staffLanes, ...orphanLanes.values()];
    }

    return [{key: 'day', day: currentDay.value, appointments: all}];
});

/**
 * A renewal reminder opens the patient's file, where the renewal plan and its
 * dates live: there is nothing to edit about the reminder itself.
 */
const openReminder = (reminder: AgendaRenewalReminder) => {
    router.push({name: 'customer.home', params: {customerId: reminder.customer.id}});
};

/**
 * Clicking a day in the month view drills down to it.
 */
const openDay = (day: Date) => {
    currentDay.value = day;
    currentView.value = 'day';
};

/**
 * The « Lien de RDV » affordance appears only where the public booking page
 * exists, which the server says: it owns the patient web host and reports
 * whether it knows one. Asked here rather than in the modal because the button
 * itself must not be offered otherwise — and the response also warms the list
 * the modal opens on.
 */
const {bookingEnabled} = useBookingLinkPresets(() => organizationId.value);
const bookingLinkOpen = ref(false);

/** The assistant is offered here only in experimental mode (Ctrl+X) for now. */
const {enabled: assistantOffered} = useExperimentalFeatures();

/** Shared by the rail and the toolbar popover, which show the same cards. */
const optionsProps = computed(() => ({
    view: currentView.value,
    compact: props.compact,
    frequentationAvailable: hasFrequentation.value && !!frequentationDisplay.value,
    birthdayCount: birthdayCount.value,
    bookingEnabled: bookingEnabled.value,
    assistantOffered: assistantOffered.value
}));

const {saving, create, reschedule} = useAppointmentMutations();
const formOpen = ref(false);
const slotStart = ref<Date | null>(null);
const detailOpen = ref(false);
const detailAppointment = ref<AgendaAppointment | null>(null);

const slotOperatorAccessId = ref<number | null>(null);

const openCreation = (payload: {start: Date; operatorAccessId?: number | null}) => {
    slotStart.value = payload.start;
    slotOperatorAccessId.value = payload.operatorAccessId ?? null;
    formOpen.value = true;
};

const handleAppointmentClick = (appointment: AgendaAppointment) => {
    detailAppointment.value = appointment;
    detailOpen.value = true;
};

/**
 * Deep link, as sent by a notification: `date` anchors the agenda on the
 * appointment's day and `appointmentId` opens its detail once the window is
 * loaded. Without a date the agenda stays on today, and the detail only
 * opens when the appointment happens to be in view.
 *
 * Only the agenda screen honours it: mounted in a patient file, the calendar
 * has no say over that route's query.
 */
const pendingAppointmentId = ref<number | null>(null);

watch(() => route.query, (query) => {
    if (props.compact) {
        return;
    }

    const date = typeof query.date === 'string' ? parseAgendaDate(query.date) : null;

    if (date?.isValid()) {
        currentDay.value = date.toDate();
    }

    const appointmentId = Number(query.appointmentId);

    pendingAppointmentId.value = Number.isInteger(appointmentId) && appointmentId > 0 ? appointmentId : null;
}, {immediate: true});

watch([appointments, pendingAppointmentId], ([loaded, pending]) => {
    if (!loaded || pending === null) {
        return;
    }

    const target = loaded.find((appointment) => appointment.id === pending);

    if (target) {
        handleAppointmentClick(target);
    }

    // Consumed on the first loaded window either way, and the URL is cleaned
    // so a reload does not reopen the detail.
    pendingAppointmentId.value = null;
    router.replace({name: 'agenda'});
    // Immediate: the window may already be served from the SWR cache, in which
    // case `appointments` never changes after setup.
}, {immediate: true});

/**
 * Create through the appointment resource, then refetch so the grid reflects
 * the change without reload. Edits go through the detail.
 */
const handleSubmit = (request: AppointmentCreateRequest, sms: PatientSmsChoice | null) => {
    const currentOrganizationId = organizationId.value;

    if (!currentOrganizationId) {
        return;
    }

    create(currentOrganizationId, request, sms, async () => {
        emit('changed');
        await refresh();
        formOpen.value = false;
    });
};

/**
 * A change made from the detail: the grid catches up without the socket. The
 * detail is already closed, so a failed refetch is said here.
 */
const handleDetailChanged = async () => {
    emit('changed');

    try {
        await refresh();
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    }
};

const rescheduleOpen = ref(false);
const rescheduledAppointment = ref<AgendaAppointment | null>(null);
const rescheduledStart = ref<Date | null>(null);

/**
 * A card was dropped on another slot: ask before touching anything, and let
 * the pharmacist choose whether the patient is texted.
 *
 * A drop in the past is refused right here: the endpoint validates
 * `after:now`, so going further would only trade the gesture for a
 * server-side rejection.
 */
const handleRescheduleRequest = (payload: {appointment: AgendaAppointment; start: Date}) => {
    if (isPastStart(payload.start)) {
        toastStore.showError({content: PAST_APPOINTMENT_MESSAGE});

        return;
    }

    // A drop clamped at the edge of its day can land on the start it had:
    // there is nothing to confirm.
    if (parseAgendaDate(payload.start).isSame(parseAgendaDate(payload.appointment.start_at), 'minute')) {
        return;
    }

    rescheduledAppointment.value = payload.appointment;
    rescheduledStart.value = payload.start;
    rescheduleOpen.value = true;
};

const handleRescheduleConfirm = (sms: PatientSmsChoice, start: Date) => {
    const appointment = rescheduledAppointment.value;

    if (!appointment) {
        return;
    }

    reschedule(appointment.id, parseAgendaDate(start).format(), sms, async () => {
        emit('changed');
        await refresh();
        rescheduleOpen.value = false;
    });
};

/**
 * A parent that changes appointments outside the grid (the timeline of the
 * patient file) refetches the window without waiting for the socket.
 */
defineExpose({refresh});
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <TooltipProvider>
      <!--
        One row whose actions never depend on the view: the display switches
        and the shortcuts live in the rail, so nothing here moves when the
        view changes. The date keeps the width of its longest label for the
        same reason.
      -->
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div class="flex items-center gap-2">
            <Button variant="outline" :disabled="isToday" @click="currentDay = new Date()">
              Aujourd'hui
            </Button>
            <Button variant="ghost" size="icon" aria-label="Jour précédent" @click="shiftDay(-1)">
              <ChevronLeft class="size-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Jour suivant" @click="shiftDay(1)">
              <ChevronRight class="size-4" />
            </Button>
            <p class="flex min-w-72 items-center gap-2 whitespace-nowrap font-medium capitalize">
              <CalendarDays class="size-4 shrink-0 text-gray-400" />
              {{ dayLabel }}
            </p>
          </div>
          <!-- The active view is filled in the brand violet: a light grey on
               white did not tell it apart from the others. -->
          <div class="flex rounded-lg border border-gray-200 bg-white p-0.5">
            <Button
              v-for="view in VIEWS"
              :key="view.key"
              :variant="currentView === view.key ? 'default' : 'ghost'"
              size="sm"
              :class="currentView === view.key ? 'bg-primary font-semibold shadow-none hover:bg-primary/90' : 'text-gray-500'"
              :aria-pressed="currentView === view.key"
              @click="currentView = view.key"
            >
              {{ view.label }}
            </Button>
          </div>
        </div>
        <!-- Below xl the labels are dropped and the tooltip carries them. -->
        <div class="ml-auto flex items-center gap-3">
          <Tooltip v-if="!compact" :delay-duration="350">
            <TooltipTrigger as-child>
              <router-link :to="{name: 'planning'}">
                <Button variant="outline" :size="labelsHidden ? 'icon' : 'default'" aria-label="Planning équipe">
                  <CalendarClock class="size-4" />
                  <span v-if="!labelsHidden">Planning équipe</span>
                </Button>
              </router-link>
            </TooltipTrigger>
            <TooltipContent side="bottom" class="text-xs">
              <p>Ouvrir le planning de l'équipe</p>
            </TooltipContent>
          </Tooltip>
          <!--
            Below xl the assistant toggle leaves the shortcuts for the toolbar,
            where it shows whether the window is up. From xl the row has no
            room to spare and the rail carries it instead.
          -->
          <AssistantLauncher v-if="labelsHidden && !compact && assistantOffered" labels-hidden />
          <!--
            Wherever the rail is not drawn, its cards open from here.

            A trigger registers itself as the anchor of the nearest popper
            root: nested in the tooltip, the popover trigger would anchor the
            tooltip and leave the popover unplaced, off screen. The explicit
            anchor sits outside the tooltip, and the trigger then registers
            none.
          -->
          <Popover v-if="railHidden">
            <PopoverAnchor class="inline-flex">
              <Tooltip :delay-duration="350">
                <TooltipTrigger as-child>
                  <PopoverTrigger as-child>
                    <Button variant="outline" size="icon" aria-label="Affichage et raccourcis">
                      <Ellipsis class="size-4" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom" class="text-xs">
                  <p>Affichage et raccourcis</p>
                </TooltipContent>
              </Tooltip>
            </PopoverAnchor>
            <PopoverContent align="end" class="w-72 border-0 bg-transparent p-0 shadow-none">
              <AgendaOptions
                v-model:presence="presenceEnabled"
                v-model:frequentation="frequentationVisible"
                v-model:birthday-overlay="birthdayOverlay"
                v-bind="optionsProps"
                floating
                @open-birthdays="openBirthdayPanel(currentDay)"
                @open-booking-link="bookingLinkOpen = true"
              />
            </PopoverContent>
          </Popover>
          <Tooltip :delay-duration="350">
            <TooltipTrigger as-child>
              <Button
                class="bg-primary"
                :size="labelsHidden ? 'icon' : 'default'"
                aria-label="Nouveau rendez-vous"
                @click="openCreation({start: defaultSlotForDay(currentDay)})"
              >
                <Plus class="size-4" />
                <span v-if="!labelsHidden">Nouveau RDV</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent v-if="labelsHidden" side="bottom" class="text-xs">
              <p>Nouveau rendez-vous</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
    <!--
      The design puts the display switches, the shortcuts and the day tasks
      in the right rail of the appointments screen. Hidden below xl: this
      grid is already dense, and stealing 288px from it on a narrow counter
      screen would cost more than the rail brings. min-w-0 lets the grid
      shrink instead of overflowing.
    -->
    <div class="flex min-h-0 flex-1 gap-4">
      <div class="min-w-0 flex-1 overflow-hidden rounded-lg bg-white shadow">
        <div
          v-if="isLoading || !organizationId || (currentView === 'team' && operatorsLoading)"
          class="flex flex-col gap-3 p-4"
        >
          <Skeleton v-for="index in 8" :key="index" class="h-12 w-full" />
        </div>
        <!--
          The error screen takes precedence over the grid even when swrv still
          holds data: after a key change the retained data belongs to the
          previous day, and rendering it under the new date is worse than an
          error message.
        -->
        <div
          v-else-if="error"
          class="flex h-full flex-col items-center justify-center gap-3 p-10 text-center"
        >
          <p class="text-sm text-gray-600">
            Impossible de charger l'agenda. Vérifiez votre connexion, puis réessayez.
          </p>
          <Button variant="outline" @click="refresh()">
            Réessayer
          </Button>
        </div>
        <AgendaMonthGrid
          v-else-if="currentView === 'month'"
          class="h-full"
          :date="currentDay"
          :appointments="appointments ?? []"
          :reminders="reminders"
          :birthdays="overlayBirthdays"
          @day-click="openDay"
          @appointment-click="handleAppointmentClick"
          @reminder-click="openReminder"
          @birthdays-click="openBirthdayPanel"
        />
        <p
          v-else-if="currentView === 'team' && !lanes.length"
          class="flex h-full items-center justify-center p-10 text-center text-sm text-gray-500"
        >
          Aucun collaborateur à afficher pour cette pharmacie.
        </p>
        <!--
          Dragging is offered on the day and week views, where a column is a day:
          the drop resolves to a new schedule. In the team view a column is a
          collaborator, so moving across one would mean reassigning the operator —
          a different mutation than a reschedule.
        -->
        <AgendaLaneGrid
          v-else
          class="h-full"
          :lanes="lanes"
          :reminders="reminders"
          :birthdays="overlayBirthdays"
          :dense="currentView !== 'day'"
          :draggable="currentView === 'day' || currentView === 'week'"
          :presence="presenceShown ? presenceLanes : undefined"
          :presence-pending="presencePending"
          :frequentation="frequentationActive ? frequentation : null"
          :frequentation-display="frequentationDisplay"
          :opening="closingHoursShown ? healthcenterOpening : null"
          @slot-click="openCreation"
          @appointment-click="handleAppointmentClick"
          @reschedule-request="handleRescheduleRequest"
          @reminder-click="openReminder"
          @birthdays-click="openBirthdayPanel"
          @presence-click="router.push('/planning')"
        />
      </div>
      <aside v-if="!railHidden" class="flex w-72 shrink-0 flex-col gap-3 overflow-y-auto">
        <AgendaOptions
          v-model:presence="presenceEnabled"
          v-model:frequentation="frequentationVisible"
          v-model:birthday-overlay="birthdayOverlay"
          v-bind="optionsProps"
          @open-birthdays="openBirthdayPanel(currentDay)"
          @open-booking-link="bookingLinkOpen = true"
        />
        <TeamTaskDayCard :current-access-id="currentAccessId" />
      </aside>
    </div>
    <!--
      No link to the analysis screen from a patient file: its back button
      returns to the agenda, which would take the pharmacist out of the file.
    -->
    <FrequentationLegend v-if="frequentationActive && !isLoading && !error">
      <router-link v-if="!compact" :to="{name: 'agenda.frequentation'}" class="font-medium text-primary hover:underline">
        Voir l'analyse →
      </router-link>
    </FrequentationLegend>
    <!-- The grey has no tooltip, being inert under the pointer: this names it. -->
    <p v-if="closingHoursShown && !isLoading && !error" class="flex items-center gap-1.5 text-xs text-gray-600">
      <span class="h-2.5 w-4 rounded-sm bg-gray-200/70" />
      Plages grisées : pharmacie fermée
    </p>
    <AgendaAppointmentFormModal
      v-model:open="formOpen"
      :opening="healthcenterOpening"
      :initial-start="slotStart"
      :initial-operator-access-id="slotOperatorAccessId"
      :initial-patient="pinnedPatient"
      :loading="saving"
      @submit="handleSubmit"
    />
    <AgendaRescheduleDialog
      v-model:open="rescheduleOpen"
      :appointment="rescheduledAppointment"
      :start="rescheduledStart"
      :loading="saving"
      @confirm="handleRescheduleConfirm"
    />
    <AgendaAppointmentDetail
      v-model:open="detailOpen"
      :appointment="detailAppointment"
      @changed="handleDetailChanged"
    />
    <AgendaBookingLinkModal v-if="bookingEnabled" v-model:open="bookingLinkOpen" />
    <AgendaBirthdayPanel
      v-model:open="birthdayPanelOpen"
      :day="birthdayPanelDay"
      :birthdays="panelBirthdays"
      :sending-ids="sendingBirthdayIds"
      :sending-all="sendingAllBirthdays"
      :gifts="activeGifts"
      :send-hour="giftSettings?.send_hour"
      :failed="birthdaysFailed"
      @create-gift="goToGiftCatalog"
      @send="sendBirthday"
      @send-all="sendAllBirthdays"
    />
  </div>
</template>
